import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  EditorDeclarativeBlock,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { mutateFn, useComplexState } from '@/utils/use-complex-state';
import {
  defaultPTProps,
  PTConfigStep,
  PTProps,
} from '@/features/editor/widgets/price-test/props';
import { EditorContext } from '@/features/editor/context/editor-context';
import { AccountContext } from '@/features/account-context';

export const PriceTestContext = React.createContext(
  {} as PriceTestContextProps,
);

export function newPriceTestContext(
  variant: ExperienceVariant,
  variantIdx: number,
  origChange?: EditorDeclarativeBlock,
): PriceTestContextProps {
  const {
    account: {
      store: { currency: defaultCurrency },
    },
  } = useContext(AccountContext);
  const {
    experienceState: {
      upsertEditorChange,
      getPriceTestChange,
      currentExperience: { targeting },
    },
  } = useContext(EditorContext);

  const currency = useMemo(
    () =>
      targeting?.other?.find(
        (prop) => prop?.qbProps?.caption === `Cart Currency`,
      )?.values?.[0]?.value ||
      defaultCurrency ||
      `USD`,
    [targeting?.other],
  );

  const originalChange = getPriceTestChange(variantIdx) || origChange;

  const [step, setStep] = useState(PTConfigStep.MATCH_STEP);

  const [change, setChange] = useComplexState(originalChange);
  const [props, setProps] = useComplexState(
    originalChange?.priceTestOpts || defaultPTProps(),
  );

  useEffect(() => {
    setChange((draft) => {
      draft.block.kind = `priceTest`;
      draft.block.selector = `div`;
      draft.block.value = ``;
      draft.priceTestOpts = props;
    });
  }, [props]);

  useEffect(() => {
    upsertEditorChange(change, false, false);
  }, [change]);

  const navToQuizStep = () => {
    setStep(PTConfigStep.QUIZ_STEP);
  };

  const navToPagesStep = () => {
    setStep(PTConfigStep.PAGES_STEP);
  };

  const navToDoneStep = () => {
    setStep(PTConfigStep.DONE_STEP);
  };

  const navToMatchStep = () => {
    setStep(PTConfigStep.MATCH_STEP);
  };

  const onNextClicked = () => {
    setStep((prev) => {
      switch (prev) {
        case PTConfigStep.MATCH_STEP:
          return PTConfigStep.QUIZ_STEP;
        case PTConfigStep.QUIZ_STEP:
          return PTConfigStep.PAGES_STEP;
        case PTConfigStep.PAGES_STEP:
          return PTConfigStep.DONE_STEP;
        default:
          return prev;
      }
    });
  };

  const onBackClicked = () => {
    setStep((prev) => {
      switch (prev) {
        case PTConfigStep.QUIZ_STEP:
          return PTConfigStep.MATCH_STEP;
        case PTConfigStep.PAGES_STEP:
          return PTConfigStep.QUIZ_STEP;
        case PTConfigStep.DONE_STEP:
          return PTConfigStep.PAGES_STEP;
        default:
          return prev;
      }
    });
  };

  const getProgress = useCallback(() => {
    switch (step) {
      case PTConfigStep.MATCH_STEP:
        return 0;
      case PTConfigStep.QUIZ_STEP:
        return 40;
      case PTConfigStep.PAGES_STEP:
        return 80;
      case PTConfigStep.DONE_STEP:
        return 100;
      default:
        return 0;
    }
  }, [step]);

  const getNextText = useCallback(() => {
    switch (step) {
      case PTConfigStep.MATCH_STEP:
      case PTConfigStep.QUIZ_STEP:
        return `Continue to the next step >`;
      case PTConfigStep.PAGES_STEP:
        return `Click to finalize variation`;
      case PTConfigStep.DONE_STEP:
        return `Done`;
      default:
        return ``;
    }
  }, [step]);

  const getNextHint = useCallback(() => {
    switch (step) {
      case PTConfigStep.MATCH_STEP:
        return `In the next step we'll ask you some yes/no questions`;
      case PTConfigStep.QUIZ_STEP:
        return `In the next step you'll choose the pages you want to run the experience on`;
      case PTConfigStep.PAGES_STEP:
        return ``;
      case PTConfigStep.DONE_STEP:
        return `All set! Click "Send Request for price testing" to continue`;
      default:
        return ``;
    }
  }, [step]);

  return {
    step,
    setStep,
    change,
    setChange,
    props,
    setProps,
    navToQuizStep,
    navToPagesStep,
    navToDoneStep,
    navToMatchStep,
    onNextClicked,
    onBackClicked,
    getProgress,
    getNextText,
    getNextHint,
    currency,
  };
}

export function formatMoneyWithLocale(amount: number, currencyCode: string) {
  if (amount === 0) return `Free`;

  const currency = currencyCode || `USD`;
  const locale = navigator?.language || `en-US`;

  const formatter = new Intl.NumberFormat(locale, {
    style: `currency`,
    currency,
    currencyDisplay: `narrowSymbol`,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
}

export interface PriceTestContextProps {
  step: PTConfigStep;
  setStep: (step: PTConfigStep) => void;

  change: EditorDeclarativeBlock;
  setChange: (fn: mutateFn<EditorDeclarativeBlock>) => void;

  props: PTProps;
  setProps: (fn: mutateFn<PTProps>) => void;

  currency: string;

  navToQuizStep: () => void;
  navToPagesStep: () => void;
  navToDoneStep: () => void;
  navToMatchStep: () => void;
  onNextClicked: () => void;
  onBackClicked: () => void;
  getProgress: () => number;

  getNextText: () => string;
  getNextHint: () => string;
}

export function getCurrencySymbol(currency: string): string {
  return new Intl.NumberFormat(navigator.language, {
    style: `currency`,
    currencyDisplay: `narrowSymbol`,
    currency,
  })
    .formatToParts(1)
    .find((x) => x.type === `currency`).value;
}
